import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { notificationService } from "../../services/notificationService";

const ADMIN_ROLES = ["ADMIN", "SUPER_ADMIN", "SUPERADMIN", "REVIEWER"];

export interface NotificationItem {
  id: string;
  type: string;
  message: string;
  metadata?: Record<string, any> | null;
  isRead: boolean;
  createdAt: string;
  updatedAt: string;
}

interface NotificationState {
  items: NotificationItem[];
  loading: boolean;
  error: string | null;
}

const initialState: NotificationState = {
  items: [],
  loading: false,
  error: null,
};

// ---- Thunks ----

export const fetchUserNotifications = createAsyncThunk<NotificationItem[], void, { state: any }>(
  "notifications/fetchUser",
  async (_, { rejectWithValue, getState }) => {
    const roleCode = (getState().auth?.currentUser?.role?.role_code ?? "").toUpperCase();
    if (ADMIN_ROLES.includes(roleCode)) return [];

    try {
      const res = await notificationService.getUserNotifications();
      const payload = res.data ?? res;
      return Array.isArray(payload) ? payload : (payload.data ?? []);
    } catch (error: any) {
      return rejectWithValue(error?.message || "Failed to fetch notifications");
    }
  }
);

export const fetchAdminNotifications = createAsyncThunk<NotificationItem[]>(
  "notifications/fetchAdmin",
  async (_, { rejectWithValue }) => {
    try {
      const res = await notificationService.getAdminNotifications();
      // Support both paginated { data: { data: [] } } and legacy flat array
      const payload = res.data ?? res;
      return Array.isArray(payload) ? payload : (payload.data ?? []);
    } catch (error: any) {
      return rejectWithValue(error?.message || "Failed to fetch admin notifications");
    }
  }
);

// ---- Slice ----

const notificationSlice = createSlice({
  name: "notifications",
  initialState,
  reducers: {
    // Ajout temps réel via WebSocket
    addNotification: (state, action: PayloadAction<Partial<NotificationItem> & { type: string; message: string }>) => {
      // Normaliser : les events WS n'ont pas d'id/isRead/dates
      const notification: NotificationItem = {
        id: action.payload.id ?? `ws-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        type: action.payload.type,
        message: action.payload.message,
        metadata: action.payload.metadata ?? null,
        isRead: action.payload.isRead ?? false,
        createdAt: action.payload.createdAt ?? new Date().toISOString(),
        updatedAt: action.payload.updatedAt ?? new Date().toISOString(),
      };
      const exists = state.items.some((n) => n.id === notification.id);
      if (!exists) {
        state.items.unshift(notification);
      }
    },
    markAsRead: (state, action: PayloadAction<string>) => {
      const notif = state.items.find((n) => n.id === action.payload);
      if (notif) notif.isRead = true;
    },
    markAllAsRead: (state) => {
      state.items.forEach((n) => { n.isRead = true; });
    },
    removeNotification: (state, action: PayloadAction<string>) => {
      state.items = state.items.filter((n) => n.id !== action.payload);
    },
    clearNotifications: (state) => {
      state.items = [];
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserNotifications.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserNotifications.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
      })
      .addCase(fetchUserNotifications.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchAdminNotifications.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAdminNotifications.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
      })
      .addCase(fetchAdminNotifications.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  addNotification,
  markAsRead,
  markAllAsRead,
  removeNotification,
  clearNotifications,
} = notificationSlice.actions;

export default notificationSlice.reducer;
